import MiniSearch from 'minisearch';
import type { FoodRow } from '@/lib/db';

/**
 * Quita acentos y pasa a minúsculas.
 *
 * Es lo que hace que "jamon york" encuentre "jamón cocido": si no se
 * normalizan las dos puntas (lo indexado y lo tecleado), nadie con prisa
 * escribiendo en un móvil encuentra nada.
 *
 * La "ñ" también se pliega a "n" a propósito: aquí no se escribe, se busca, y
 * quien teclea "pina" quiere encontrar "piña". Esta función NUNCA se usa para
 * mostrar texto.
 */
export function normalize(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/**
 * Pliega el plural y la -e final: "manzanas"/"manzana", "limones"/"limon",
 * "nueces"/"nuez", "tomates"/"tomate" acaban en la misma forma.
 *
 * Tosco a propósito y, como `normalize`, solo para emparejar. Sin esto,
 * "manzana" solo casaba como prefijo con "Manzanas" y le ganaba "Zumo de
 * manzana", donde casaba exacto.
 */
export function stem(word: string): string {
  const out = word.replace(/(es|s|e)$/, '').replace(/z$/, 'c');
  return out.length >= 3 ? out : word;
}

const STOP_WORDS = new Set(['de', 'del', 'la', 'el', 'los', 'las', 'con', 'sin', 'al', 'a', 'y']);

/** Palabras con significado, normalizadas y sin plural. */
export function keyWords(text: string): string[] {
  return normalize(text)
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length > 2 && !STOP_WORDS.has(w))
    .map(stem);
}

/**
 * Formas que no son el alimento tal cual: en polvo, secas, instantáneas o
 * postres helados. Su valor por 100 g no se parece al del alimento fresco
 * ("Huevo, seco" es huevo en polvo: cuatro veces las kcal).
 */
export const CONCENTRATE =
  /en polvo|deshidratad|liofiliz|instant[áa]ne|\bsec[oa]s?\b|extracto|concentrad|\bhelad[oa]s?\b/i;

export interface SearchDoc {
  id: string;
  name: string;
  synonyms: string;
  tags: string;
}

function toDoc(food: FoodRow): SearchDoc {
  return {
    id: food.id,
    name: food.name,
    // Con coma para poder separarlos al ordenar; al indexar la coma ya parte palabras.
    synonyms: food.synonyms.join(', '),
    tags: food.tags.join(' '),
  };
}

export function buildIndex(foods: readonly FoodRow[]): MiniSearch<SearchDoc> {
  const index = new MiniSearch<SearchDoc>({
    fields: ['name', 'synonyms', 'tags'],
    storeFields: ['id', 'name', 'synonyms', 'tags'],
    processTerm: (term) => {
      const clean = normalize(term);
      // Las palabras de una letra solo añaden ruido.
      return clean.length > 1 ? stem(clean) : null;
    },
    tokenize: (text) => text.split(/[\s,/()]+/).filter(Boolean),
    searchOptions: {
      prefix: true,
      // Tolerancia a erratas proporcional a la longitud: en "brocoli" perdona
      // una letra, en "pan" ninguna, que si no todo se parece a todo.
      fuzzy: (term) => (term.length > 5 ? 0.2 : term.length > 3 ? 0.15 : 0),
      boost: { name: 3, synonyms: 2, tags: 1 },
      combineWith: 'AND',
    },
  });

  index.addAll(foods.map(toDoc));
  return index;
}

/** Mete, cambia o quita un alimento de un índice ya construido, sin rehacerlo. */
export function indexFood(index: MiniSearch<SearchDoc>, food: FoodRow): void {
  if (food.deletedAt != null) {
    if (index.has(food.id)) index.discard(food.id);
    return;
  }
  if (index.has(food.id)) index.replace(toDoc(food));
  else index.add(toDoc(food));
}

export interface SearchOptions {
  limit?: number;
  /** IDs que van primero si aparecen: recientes y favoritos del usuario. */
  preferred?: ReadonlySet<string>;
}

interface Hit {
  id: string;
  /** El nombre y sus sinónimos: todos son formas de llamar al mismo alimento. */
  names: string[];
  /** 1 si es de los alimentos corrientes revisados a mano (COMMON_FOODS del seed). */
  common: number;
  score: number;
}

/** MiniSearch tipa los campos guardados como `any`; se estrechan aquí, en la frontera. */
function run(index: MiniSearch<SearchDoc>, query: string, combineWith?: 'AND' | 'OR'): Hit[] {
  const raw = combineWith ? index.search(query, { combineWith }) : index.search(query);
  return raw.map((r) => ({
    id: String(r.id),
    names: [String(r.name), ...String(r.synonyms).split(',')],
    common: String(r.tags).split(' ').includes('comun') ? 1 : 0,
    score: r.score,
  }));
}

export function searchFoods(
  index: MiniSearch<SearchDoc>,
  query: string,
  { limit = 40, preferred }: SearchOptions = {},
): string[] {
  const clean = query.trim();
  if (clean.length < 2) return [];

  let results = run(index, clean);

  // Si la búsqueda estricta no da nada, se relaja a OR antes de rendirse:
  // "pechuga pollo crudo" no debería devolver cero por una palabra de más.
  if (results.length === 0) results = run(index, clean, 'OR');

  // Los nombres son "Cabeza, matiz, matiz": la primera palabra dice QUÉ es el
  // alimento. Se sube lo que ES lo pedido sobre lo que solo lo lleva dentro
  // ("Manzanas" antes que "Zumo de manzana"), y lo que se come tal cual sobre
  // su versión en polvo o seca. `startsWith` para que funcione mientras se
  // teclea. Lo que el usuario ya suele comer va antes que todo.
  const asked = keyWords(clean);
  const rank = (hit: Hit) => {
    const heads = hit.names.map((name) => keyWords(name)[0] ?? '');
    const isIt = heads.some((head) => asked.some((w) => head.startsWith(w)));
    return (
      (preferred?.has(hit.id) ? 4 : 0) + (isIt ? 2 : 0) + (CONCENTRATE.test(hit.names[0]!) ? 0 : 1)
    );
  };
  // Desempate: el alimento corriente (revisado a mano) antes que la variante
  // rara del mismo alimento; si no, ganaba el nombre más corto ("Leche de oveja").
  results.sort((a, b) => rank(b) - rank(a) || b.common - a.common || b.score - a.score);

  return results.slice(0, limit).map((r) => r.id);
}
